import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Eye, MapPin, Repeat, ArrowRight, RotateCcw } from "lucide-react";

const H = { fontFamily: "'Outfit', 'Poppins', sans-serif", fontWeight: 800 };
const B = { fontFamily: "'Poppins', sans-serif" };

const ROBBIE = "/img/mascots/robbie-winkt.webp";
const WA_LINK = "https://wa.me/4952211412070?text=Hallo%20Robbie";
const WA_DISPLAY = "05221 1412070";

const slug = (x) => x.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/* Kreislauf-Prinzip „Sehen. Kommen. Wiederkommen." – emotionaler Kundenreise-Flow statt kaltem Kreisdiagramm.
   Desktop: drei Etappenkarten nebeneinander, verbunden durch animierte Fluss-Pfeile + sichtbarer Rückkehr-Bogen;
   Robbie schwebt als „Antrieb" darüber. Mobile: senkrechte Kette mit einblendenden Karten.
   Framer Motion; prefers-reduced-motion = ruhiger statischer Endzustand. */

const STATIONEN = [
  { id: "sehen", n: 1, icon: Eye, titel: "Sehen", text: "Deine Kunden sehen dich – online und vor dem Laden.",
    chips: [
      ["Instagram", "/social"], ["Facebook", "/social"], ["TikTok", "/tiktok"], ["LinkedIn", "/social"],
      ["Google", "/google"], ["Website & Mini-Website", "/homepage"], ["Bildschirm im Laden", "/digital-signage"], ["Video & Reels", "/videostudio"],
    ] },
  { id: "kommen", n: 2, icon: MapPin, titel: "Kommen", text: "Aus einem Blick wird ein Besuch.",
    chips: [
      ["Aktionen & Angebote", "/social"], ["Google-Bewertungen", "/google"], ["Tickets & Reservierung", "/tickets"],
    ] },
  { id: "wiederkommen", n: 3, icon: Repeat, titel: "Wiederkommen", text: "Wer einmal da war, kommt wieder.",
    chips: [
      ["Kundenkarte in Apple & Google Wallet", "/kundenkarte"], ["Newsletter", "/fallbeispiele/newsletter"], ["WhatsApp", "/robbie"],
    ],
    wallet: true },
];

const RobbieHub = ({ mobil = false, reduce }) => (
  <div className="relative flex flex-col items-center text-center" data-testid="kreis-robbie">
    <div className="pointer-events-none absolute -top-2 h-28 w-28 rounded-full bg-[#FDD105]/40 blur-2xl" aria-hidden="true" />
    <motion.img
      src={ROBBIE} alt="Robbie, der digitale Mitarbeiter" width="160" height="160" loading="lazy"
      className={`relative ${mobil ? "w-20" : "w-28 lg:w-32"} drop-shadow-[0_16px_28px_rgba(0,0,0,0.18)]`}
      animate={reduce ? undefined : { y: [0, -7, 0] }}
      transition={reduce ? undefined : { duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
    />
    <div className="relative mt-3 rounded-2xl border-2 border-[#111] bg-[#FDD105] px-3.5 py-2 shadow-[3px_3px_0_0_#111] max-w-[230px]">
      <span className="absolute -top-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-l-2 border-t-2 border-[#111] bg-[#FDD105]" aria-hidden="true" />
      <p style={{ ...B, fontWeight: 600 }} className="text-[12.5px] leading-tight text-[#111]">Ich halte den Kreislauf am Laufen – du gibst nur frei.</p>
    </div>
    <a href={WA_LINK} target="_blank" rel="noreferrer" data-testid="kreis-wa-badge"
      className="relative mt-2.5 inline-flex items-center gap-1.5 rounded-full border-2 border-[#111] bg-white px-3 py-1.5 text-[12px] font-bold text-[#111] shadow-[2px_2px_0_0_#111] transition-transform hover:-translate-y-0.5">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="#25D366" aria-hidden="true"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8s-.4-.1-.6.1-.6.8-.8 1-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.3-.4.8-1.4.1-.2 0-.3 0-.5l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4 5.2 5.2 0 0 0 3.1.6 2.6 2.6 0 0 0 1.7-1.2 2.1 2.1 0 0 0 .2-1.2c-.1-.1-.3-.2-.5-.3Z"/></svg>
      {WA_DISPLAY}
    </a>
  </div>
);

const Chip = ({ to, children, testId }) => (
  <Link to={to} data-testid={testId} style={{ ...B, fontWeight: 600 }}
    className="inline-flex items-center rounded-full border border-[#111]/20 bg-[#FAF8F3] px-2.5 py-1 text-[11px] leading-none text-[#111] transition-all duration-200 hover:-translate-y-0.5 hover:scale-[1.04] hover:border-[#111] hover:bg-[#FDD105]">
    {children}
  </Link>
);

const StationCard = ({ s, variants, testIdPrefix = "kreis-station" }) => {
  const Icon = s.icon;
  return (
    <motion.div variants={variants}
      className="group relative flex flex-1 flex-col rounded-[24px] border-2 border-[#111] bg-white p-5 shadow-[6px_6px_0_0_#111] transition-transform duration-300 hover:-translate-y-1.5"
      data-testid={`${testIdPrefix}-${s.id}`}>
      <span style={H} className="pointer-events-none absolute right-4 top-3 text-[42px] leading-none text-[#111]/5 transition-colors duration-300 group-hover:text-[#FDD105]/70" aria-hidden="true">{`0${s.n}`}</span>
      <span className="flex items-center gap-2.5">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full border-2 border-[#111] bg-[#FDD105] text-[#111] shadow-[2px_2px_0_0_#111] transition-transform duration-300 group-hover:rotate-6"><Icon size={19} strokeWidth={2.2} aria-hidden="true" /></span>
        <span style={H} className="text-xl text-[#111]">{s.titel}</span>
      </span>
      <p style={B} className="mt-3 text-[14px] leading-relaxed text-[#4A4A4A]">{s.text}</p>
      <div className="mt-3.5 flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-${s.id}-chips`}>
        {s.chips.map(([label, to]) => <Chip key={label} to={to} testId={`${testIdPrefix}-${s.id}-chip-${slug(label)}`}>{label}</Chip>)}
      </div>
      {s.wallet && (
        <div className="mt-3.5 flex flex-wrap items-center gap-2" data-testid={`${testIdPrefix}-${s.id}-wallet`}>
          <Link to="/kundenkarte" aria-label="Zu Apple Wallet hinzufügen" data-testid={`${testIdPrefix}-${s.id}-apple`} className="transition-transform hover:-translate-y-0.5">
            <img src="/wallet/apple-wallet-de.svg" alt="Zu Apple Wallet hinzufügen" className="block h-8 w-auto" />
          </Link>
          <Link to="/kundenkarte" aria-label="Zu Google Wallet hinzufügen" data-testid={`${testIdPrefix}-${s.id}-google`} className="transition-transform hover:-translate-y-0.5">
            <img src="/wallet/google-wallet-de.svg" alt="Zu Google Wallet hinzufügen" className="block h-8 w-auto" />
          </Link>
        </div>
      )}
    </motion.div>
  );
};

const FlowArrow = ({ reduce }) => (
  <motion.div className="relative z-10 hidden shrink-0 self-center lg:block" aria-hidden="true"
    animate={reduce ? undefined : { x: [0, 6, 0] }}
    transition={reduce ? undefined : { duration: 1.5, repeat: Infinity, ease: "easeInOut" }}>
    <span className="grid h-10 w-10 place-items-center rounded-full border-2 border-[#111] bg-[#FDD105] shadow-[2px_2px_0_0_#111]">
      <ArrowRight size={18} strokeWidth={2.8} className="text-[#111]" />
    </span>
  </motion.div>
);

export const KreislaufPrinzip = () => {
  const reduce = useReducedMotion();
  const reveal = reduce ? {} : { initial: "hidden", whileInView: "show", viewport: { once: true, margin: "-80px" } };
  const rowV = reduce ? {} : { hidden: {}, show: { transition: { staggerChildren: 0.14, delayChildren: 0.05 } } };
  const cardV = reduce ? undefined : { hidden: { opacity: 0, y: 26, scale: 0.96 }, show: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 120, damping: 16 } } };
  const headV = reduce ? {} : { hidden: { opacity: 0, y: -14 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };
  const popV = reduce ? {} : { hidden: { opacity: 0, scale: 0.8 }, show: { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 240, damping: 18, delay: 0.15 } } };

  return (
    <section id="prinzip" className="scroll-mt-24 bg-white" data-testid="kreislauf-prinzip">
      <div className="mx-auto max-w-[1180px] px-6 py-10 md:px-10 md:py-24">
        <motion.div className="max-w-[680px]" variants={headV} {...reveal}>
          <span style={{ ...B, fontWeight: 700 }} className="inline-block rounded-full border-2 border-[#111] bg-[#FDD105] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#111]">Das Prinzip</span>
          <h2 style={H} className="mt-4 text-[#111] leading-tight text-3xl md:text-4xl" data-testid="kreis-headline">Sehen. Kommen. <span className="box-decoration-clone bg-[#FDD105] px-2">Wiederkommen.</span></h2>
          <p style={B} className="mt-3 text-[15px] leading-relaxed text-[#4A4A4A]" data-testid="kreis-subline">Drei Schritte, die aus Laufkundschaft Stammkundschaft machen. Robbie kümmert sich um alle drei – du steuerst per WhatsApp.</p>
        </motion.div>

        {/* Desktop: Robbie als Antrieb über dem Etappen-Flow */}
        <div className="mt-10 hidden lg:block" data-testid="kreis-desktop">
          <motion.div className="mb-9 flex justify-center" variants={popV} {...reveal}>
            <RobbieHub reduce={reduce} />
          </motion.div>

          <motion.div className="flex items-stretch gap-3" variants={rowV} {...reveal}>
            <StationCard s={STATIONEN[0]} variants={cardV} />
            <FlowArrow reduce={reduce} />
            <StationCard s={STATIONEN[1]} variants={cardV} />
            <FlowArrow reduce={reduce} />
            <StationCard s={STATIONEN[2]} variants={cardV} />
          </motion.div>

          {/* Rückkehr-Bogen: der Kreislauf schließt sich */}
          <motion.div className="relative mt-1 h-[86px]" variants={headV} {...reveal} data-testid="kreis-zurueck">
            <svg viewBox="0 0 1000 80" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden="true">
              <motion.path d="M956 6 C 956 70, 44 70, 44 6" fill="none" stroke="#FDD105" strokeWidth="3" strokeLinecap="round" strokeDasharray="2 14"
                animate={reduce ? undefined : { strokeDashoffset: [0, -64] }}
                transition={reduce ? undefined : { duration: 1.6, repeat: Infinity, ease: "linear" }} />
              <polygon points="44,2 38,14 50,14" fill="#FDD105" stroke="#111" strokeWidth="1" strokeLinejoin="round" />
            </svg>
            <span style={{ ...B, fontWeight: 600 }} className="absolute left-1/2 top-[46px] -translate-x-1/2 whitespace-nowrap rounded-full border-2 border-[#111] bg-white px-4 py-1.5 text-[13px] text-[#111] shadow-[2px_2px_0_0_#111]">
              <RotateCcw size={14} className="mr-1.5 inline text-[#111]" aria-hidden="true" />…und wieder von vorn: zurück zu „Sehen“.
            </span>
          </motion.div>
        </div>

        {/* Mobile: senkrechte Kette */}
        <div className="relative mt-7 lg:hidden" data-testid="kreis-mobile">
          <div className="mb-5 flex justify-center"><RobbieHub mobil reduce={reduce} /></div>
          <motion.div className="relative pl-10" variants={rowV} {...reveal}>
            <motion.div className="absolute left-[18px] top-2 bottom-10 w-[3px] origin-top rounded-full bg-[#FDD105]" aria-hidden="true"
              initial={reduce ? false : { scaleY: 0 }} whileInView={reduce ? undefined : { scaleY: 1 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: "easeOut" }} />
            <div className="flex flex-col gap-4">
              {STATIONEN.map((s) => (
                <div key={s.id} className="relative">
                  <span className="absolute -left-[34px] top-5 z-10 grid h-8 w-8 place-items-center rounded-full border-2 border-[#111] bg-[#FDD105] text-[13px] font-bold text-[#111]" style={H} aria-hidden="true">{s.n}</span>
                  <StationCard s={s} variants={cardV} testIdPrefix="kreis-station-mobile" />
                </div>
              ))}
            </div>
          </motion.div>
          <div className="mt-4 flex items-center justify-center gap-2 rounded-full border-2 border-dashed border-[#111]/40 bg-[#FAF8F3] px-4 py-2.5">
            <motion.span animate={reduce ? undefined : { rotate: [0, -360] }} transition={reduce ? undefined : { duration: 6, repeat: Infinity, ease: "linear" }}>
              <RotateCcw size={16} className="text-[#111]" aria-hidden="true" />
            </motion.span>
            <span style={{ ...B, fontWeight: 600 }} className="text-[14px] text-[#111]">…und wieder von vorn: zurück zu „Sehen“.</span>
          </div>
        </div>

        {/* Außerdem in jedem Paket */}
        <div className="mt-10 flex flex-col items-start gap-2 rounded-2xl border-2 border-[#111] bg-[#FAF8F3] px-5 py-4 shadow-[3px_3px_0_0_#111] sm:flex-row sm:items-center sm:justify-between" data-testid="kreis-plus">
          <p style={{ ...B, fontWeight: 600 }} className="text-[14px] text-[#111]">Außerdem in jedem Paket: <span style={{ fontWeight: 700 }}>Büro &amp; Belege, Cloud, Hosting.</span></p>
          <Link to="/preise" className="group inline-flex shrink-0 items-center gap-1.5 text-[13px] font-bold text-[#111] underline decoration-[#FDD105] decoration-[3px] underline-offset-4" style={{ ...B, fontWeight: 700 }} data-testid="kreis-alle-leistungen">
            Alle Leistungen <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
};
